import { notification } from 'antd'
import { Point } from 'types/building'

/**
 * Retrieves the active pointVersionId with fallback priority:
 * 1. pointDetail.pointVersionId (if matching current point or available)
 * 2. points list array item.pointVersionId
 * 3. Fallback 0
 */
export const getPointVersion = (
  currentPointId?: string,
  pointDetail?: { id?: string; pointVersionId?: number },
  points?: Point[]
): number => {
  if (pointDetail?.pointVersionId != null) {
    if (!currentPointId || String(pointDetail.id) === String(currentPointId)) {
      return pointDetail.pointVersionId
    }
  }
  const foundPoint = points?.find((p) => p.id === currentPointId)
  return foundPoint?.pointVersionId ?? pointDetail?.pointVersionId ?? 0
}

/**
 * Safely extracts pointVersionId as a valid integer number from an API response object or value.
 * Guarantees undefined (never NaN) if invalid or missing.
 */
export const getPointVersionFromResponse = (res: any): number | undefined => {
  if (res == null) return undefined
  const raw =
    typeof res === 'number' || typeof res === 'string'
      ? res
      : res?.data?.pointVersionId ?? res?.pointVersionId
  if (raw == null) return undefined
  const parsed = Number(raw)
  return Number.isNaN(parsed) ? undefined : Math.floor(parsed)
}

/**
 * Centralized sync of updated pointVersionId across pointDetail and listPointState Recoil stores.
 */
export const syncPointVersion = (
  newVersion: number | undefined,
  currentPointId: string | undefined,
  setPointDetail: React.Dispatch<React.SetStateAction<any>>,
  setPoints?: React.Dispatch<React.SetStateAction<any>>
) => {
  if (newVersion == null) return

  setPointDetail(
    (prev: any) =>
      prev && {
        ...prev,
        pointVersionId: newVersion
      }
  )

  if (setPoints && currentPointId) {
    setPoints((prev: Point[]) =>
      prev.map((item: Point) =>
        String(item.id) === String(currentPointId)
          ? { ...item, pointVersionId: newVersion }
          : item
      )
    )
  }
}

/**
 * Checks if an error is a point_version_mismatch error and displays the standard error notification.
 * Returns true if handled, false otherwise.
 */
export const handlePointVersionError = (
  error: any,
  t?: (key: string) => string
): boolean => {
  if (error?.response?.data?.error?.key === 'point_version_mismatch') {
    notification.error({
      message: t ? t('building.pointVersionMismatch') : '',
      key: 'point_version_mismatch'
    })
    return true
  }
  return false
}

/**
 * Checks if an error is a point_is_locked_by_another error and displays the standard error notification.
 * Returns true if handled, false otherwise.
 */
export const handlePointLockedError = (error: any, t?: any): boolean => {
  if (error?.response?.data?.error?.key === 'point_is_locked_by_another') {
    notification.error({
      message: t
        ? t('building.pointIsLockedByAnother')
        : '他の人は編集中ため、ロックされます。',
      key: 'point_is_locked_by_another'
    })
    return true
  }
  return false
}
